/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 **/

import React, { JSX, ReactNode, useState, useEffect, useRef } from 'react'

import { User } from '@auth0/auth0-react'
import { FaChevronDown } from 'react-icons/fa'

import { Button } from '@/components/ui'
import { useCompany } from '@/hooks'

import { ProfilePicture } from './ProfilePicture'

export interface ProfileMenuOption {
  active?: boolean
  badge?: JSX.Element
  disabled?: boolean
  icon?: ReactNode
  key: string
  label: string
  url?: string
}

export type ProfileMenuProps = {
  goToUrl: (url: string) => void
  options?: ProfileMenuOption[]
  user?: User
  originalUser?: { firstName: string; lastName: string; email: string } | null
  className?: string
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({ goToUrl, options, user, originalUser, className }) => {
  const [isOpen, setIsOpen] = useState(false)

  const menuRef = useRef<HTMLDivElement>(null)
  const { companies, isLoading } = useCompany()
  const company = companies.find((c) => String(c.id) === user?.company)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleClick = (option: ProfileMenuOption) => {
    if (option?.disabled) return
    option.active = true
    if (option?.url) {
      goToUrl(option.url)
    }
  }
  const renderOption = (option: ProfileMenuOption) => {
    const renderLabel = () => (option?.label ? option.label : null)
    const renderIcon = () => (option?.icon ? option.icon : null)
    const renderBadge = () => (option?.badge ? option.badge : null)

    if (option?.disabled) return null
    if (option.active) {
      return (
        <strong>
          {renderIcon()}
          {renderLabel()}
          {renderBadge()}
        </strong>
      )
    } else {
      return (
        <>
          {renderIcon()}
          {renderLabel()}
          {renderBadge()}
        </>
      )
    }
  }

  return (
    <div ref={menuRef} className={`relative ${className || ''}`}>
      {/*  wraps both button and menu */}
      <div
        className={`bg-base-100 text-base-content transition-all duration-100 rounded-3xl ${
          isOpen ? 'rounded-t-none shadow-lg' : ''
        }`}
      >
        {user ? (
          <Button
            handleClick={() => setIsOpen(!isOpen)}
            variation="ghost"
            className="w-full no-animation bg-transparent hover:bg-transparent border-none shadow-none text-left flex
  flex-row md:gap-4 p-3"
          >
            <div className="flex flex-row-reverse justify-end gap-2">
              <div className="text-sm hidden md:flex flex-col">
                <span className="font-bold">{user.username}</span>
                {!originalUser && (
                  <>
                    {isLoading ? (
                      <div className="loading loading-dots loading-xs text-base-content/30"></div>
                    ) : (
                      <span className="font-light">{company?.name || ''}</span>
                    )}
                  </>
                )}
                {originalUser && (
                  <span
                    className="text-xs text-error font-medium truncate"
                    title={`Logged in as ${originalUser.firstName} ${originalUser.lastName}`}
                  >
                    via {originalUser.firstName} {originalUser.lastName}
                  </span>
                )}
              </div>
              <ProfilePicture user={user as User & { profileImage?: string; picture?: string }} />
            </div>
            <FaChevronDown size={10} className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
          </Button>
        ) : null}
      </div>

      <div
        className={`absolute bottom-full left-0 md:w-full bg-base-100 rounded-t-2xl overflow-hidden transition-all
  duration-100 ease-in-out z-50 shadow-lg ${isOpen ? 'max-h-64 pt-2 w-32' : 'max-h-0 shadow-none'}`}
      >
        <ul className="menu menu-sm p-2 pt-0 text-base-content w-full">
          {options?.map((option: ProfileMenuOption) => (
            <li key={option.key}>
              <a onClick={() => handleClick(option)} className="hover:bg-base-200 transition-colors">
                {renderOption(option)}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export { ProfileMenu }
